import React, { useState, useEffect } from "react";
import { notification, message, Button, Progress, Typography } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  SaveOutlined,
  CloudSyncOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

// Configure notification globally
notification.config({
  placement: "topRight",
  duration: 4,
  maxCount: 5,
  rtl: false,
});

// Toast notification system
export type ToastSeverity = "success" | "error" | "warning" | "info";

export interface ToastMessage {
  id: string;
  message: string;
  severity: ToastSeverity;
  duration?: number;
  action?: React.ReactNode;
}

interface ToastContextType {
  showToast: (
    message: string,
    severity?: ToastSeverity,
    duration?: number
  ) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const ToastContext = React.createContext<ToastContextType | null>(null);

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const showToast = (
    message: string,
    severity: ToastSeverity = "info",
    duration = 4
  ) => {
    const config = {
      message: message,
      duration: duration,
      className: "animate-slide-up",
    };

    switch (severity) {
      case "success":
        notification.success({
          ...config,
          icon: <CheckCircleOutlined className="text-green-500" />,
        });
        break;
      case "error":
        notification.error({
          ...config,
          duration: 6,
          icon: <ExclamationCircleOutlined className="text-red-500" />,
        });
        break;
      case "warning":
        notification.warning({
          ...config,
          duration: 5,
          icon: <WarningOutlined className="text-yellow-500" />,
        });
        break;
      case "info":
      default:
        notification.info({
          ...config,
          icon: <InfoCircleOutlined className="text-blue-500" />,
        });
        break;
    }
  };

  const showSuccess = (message: string) => showToast(message, "success");
  const showError = (message: string) => showToast(message, "error");
  const showWarning = (message: string) => showToast(message, "warning");
  const showInfo = (message: string) => showToast(message, "info");

  return (
    <ToastContext.Provider
      value={{ showToast, showSuccess, showError, showWarning, showInfo }}
    >
      {children}
    </ToastContext.Provider>
  );
};

// Save indicator component
interface SaveIndicatorProps {
  isSaving: boolean;
  lastSaved: Date | null;
  status: "idle" | "saving" | "saved" | "error";
}

export const SaveIndicator: React.FC<SaveIndicatorProps> = ({
  isSaving,
  lastSaved,
  status,
}) => {
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (status === "saved") {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const getStatusColor = () => {
    switch (status) {
      case "saving":
        return "text-blue-600";
      case "saved":
        return "text-green-600";
      case "error":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case "saving":
        return <SaveOutlined className="animate-pulse" />;
      case "saved":
        return (
          <CloudSyncOutlined className={showSuccess ? "animate-bounce" : ""} />
        );
      case "error":
        return <ExclamationCircleOutlined />;
      default:
        return <SaveOutlined />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case "saving":
        return "Saving...";
      case "saved":
        return lastSaved ? `Saved ${lastSaved.toLocaleTimeString()}` : "Saved";
      case "error":
        return "Save failed";
      default:
        return "Not saved";
    }
  };

  if (isSaving) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-600 rounded-md animate-pulse">
        <SaveOutlined className="text-sm" />
        <Text className="text-xs">Saving...</Text>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-2 transition-all duration-300 ${getStatusColor()}`}
    >
      <span className={showSuccess ? "animate-bounce-subtle" : ""}>
        {getStatusIcon()}
      </span>
      <Text className={`text-xs ${getStatusColor()}`}>{getStatusText()}</Text>
    </div>
  );
};

// Loading button with animation
interface LoadingButtonProps {
  loading: boolean;
  children: React.ReactNode;
  onClick: () => void;
  type?: "primary" | "default" | "dashed" | "link" | "text";
  danger?: boolean;
  disabled?: boolean;
  size?: "small" | "middle" | "large";
  className?: string;
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading,
  children,
  onClick,
  type = "primary",
  danger = false,
  disabled = false,
  size = "middle",
  className = "",
}) => {
  const [clicked, setClicked] = useState(false);

  const handleClick = () => {
    if (!loading && !disabled) {
      setClicked(true);
      onClick();
      setTimeout(() => setClicked(false), 300);
    }
  };

  return (
    <Button
      type={type}
      danger={danger}
      size={size}
      loading={loading}
      disabled={disabled}
      onClick={handleClick}
      className={`transition-transform duration-150 ${
        clicked ? "scale-95" : "scale-100"
      } ${className}`}
    >
      {children}
    </Button>
  );
};

// Focus indicator component
export const FocusIndicator: React.FC<{
  visible: boolean;
  className?: string;
}> = ({ visible, className = "" }) => (
  <div
    className={`absolute -inset-0.5 border-2 border-primary-500 rounded pointer-events-none transition-opacity duration-200 ${
      visible ? "opacity-100" : "opacity-0"
    } ${className}`}
  />
);

// Progress indicator with smooth animation
interface ProgressIndicatorProps {
  value: number;
  label?: string;
  color?: string;
  strokeWidth?: number;
  showInfo?: boolean;
  className?: string;
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  value,
  label,
  color,
  strokeWidth = 8,
  showInfo = true,
  className = "",
}) => {
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValue(value);
    }, 100);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <Text className="block mb-2 text-sm font-medium">
          {label} {showInfo && `(${Math.round(animatedValue)}%)`}
        </Text>
      )}

      <Progress
        percent={animatedValue}
        strokeColor={color || "#003366"}
        strokeWidth={strokeWidth}
        showInfo={showInfo && !label}
        className="fade-in"
      />
    </div>
  );
};



// Quick message helpers
const quickMessage = {
  success: (content: string) => message.success(content),
  error: (content: string) => message.error(content),
  warning: (content: string) => message.warning(content),
  info: (content: string) => message.info(content),
  loading: (content: string) => message.loading(content),
};

// Export all components and hooks
export { quickMessage };
